'use client'

import { useAuth } from '@clerk/nextjs'
import { usePathname, useRouter } from 'next/navigation'
import { useConvexAuth, useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import Sidebar from '@/components/Sidebar'
import MobileTopNav from '@/components/MobileTopNav'
import CommandPalette from '@/components/CommandPalette'
import DesktopRecommendedBanner from '@/components/DesktopRecommendedBanner'
import TrialBanner from '@/components/billing/TrialBanner'
import OutOfRunsBanner from '@/components/billing/OutOfRunsBanner'
import SuspensionPaywall from '@/components/billing/SuspensionPaywall'
import InactiveAccountScreen from '@/components/InactiveAccountScreen'
import MobileUnavailableNotice from '@/components/MobileUnavailableNotice'
import OnboardingLoadingScreen from '@/components/onboarding/OnboardingLoadingScreen'
import PortalAccessDeniedScreen from '@/components/PortalAccessDeniedScreen'
import PortalShell from '@/components/PortalShell'
import ErrorBoundary from '@/components/ErrorBoundary'
import { HelpProvider } from '@/components/HelpProvider'
import { AssistantWidget } from '@/components/assistant/AssistantWidget'
import { MobileAssistantChat, MOBILE_CHAT_STRIP_CLEARANCE } from '@/components/assistant/MobileAssistantChat'
import { useState, useEffect } from 'react'
import useLogRocket from '@/hooks/useLogRocket'
import useIsMobileViewport from '@/hooks/useIsMobileViewport'
import useOrganizationPermissions from '@/hooks/useOrganizationPermissions'
import usePortal from '@/hooks/usePortal'
import { AnalyticsEvent, consumeSignupPending, trackEvent } from '@/lib/analytics'

export default function ConditionalLayout({
  children,
}: {
  children: React.ReactNode
}) {
  useLogRocket()
  const { isSignedIn, isLoaded } = useAuth()
  const { hasModule } = useOrganizationPermissions()
  const pathname = usePathname()
  const router = useRouter()
  const [collapsed, setCollapsed] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
  // Phone-width viewports get a stripped-down shell: top nav (logo + user
  // menu), page content, and a bottom chat strip — no sidebar. Tablets and
  // small desktops (>= 768px) keep the regular collapsed-sidebar layout.
  const isMobile = useIsMobileViewport()
  // Which standalone portal host we're on (admin.42a.ai / partners.42a.ai), or
  // null on the regular app host. The host decides the portal; access is gated
  // by role below, independent of organization membership.
  const portal = usePortal()

  // Gate authed queries on Convex's own auth state, NOT Clerk's isSignedIn.
  // During sign-out Clerk's isSignedIn can briefly stay true after Convex has
  // already dropped the auth token; gating on isSignedIn lets these queries fire
  // unauthenticated and throw `Authentication required`. Because they run at the
  // top of ConditionalLayout (the root layout subtree), that throw unmounts the
  // whole app into global-error — the production logout crash (React #310).
  // Convex's isAuthenticated flips in lockstep with the token, closing the race;
  // skipped queries return undefined, so the loading guards below show a spinner
  // until Clerk catches up and the sign-out redirect fires.
  const { isAuthenticated } = useConvexAuth()
  const queryArgs = isAuthenticated ? {} : 'skip'
  const hasCompletedOnboarding = useQuery(api.organizations.hasCompletedOnboarding, queryArgs)
  const organizations = useQuery(api.organizations.getOrganizations, queryArgs)
  const orgStatus = useQuery(api.organizations.getCurrentOrgStatus, queryArgs)
  const currentUser = useQuery(api.users.getCurrentUser, queryArgs)
  // Derive both signals from orgStatus (now impersonation-aware after backend fix),
  // so isSuspended and isInactive always describe the same org — the impersonated
  // org for operators, or the caller's own org for everyone else.
  const isSuspended = orgStatus?.isSuspended === true
  const isInactive = orgStatus?.state === 'inactive'
  const isMissingOrg = orgStatus?.state === 'no_org'
  // True only once currentUser has loaded and has a partnerId set. partnerId is
  // the EFFECTIVE partner, so it is already null for a user whose partner a 42a
  // operator has deactivated — that team loses the portal entry point, the
  // /partners redirect and the portal host in one go.
  const isPartnerUser = currentUser !== undefined && !!currentUser?.partnerId
  // ...and this says WHY they lost it, so the denial screen can name the cause
  // instead of claiming they were never a partner. Never grants access.
  const isPartnerDisabled = currentUser?.partnerAccessDisabled === true
  // Operators must be able to inspect inactive/suspended orgs they impersonate —
  // they must not be bounced to a paywall. Mirror the isInactiveExemptPage pattern
  // but keyed on caller type rather than route.
  const isOperator = currentUser?.userType === 'operator_42a'
  // Admin-console audience: full operators AND internal 42A agents. Mirrors
  // AdminLayout's own gate (useUserPermissions().isAdmin). Used for admin-portal
  // access and for the no-org carve-out so a super admin can reach /admin even
  // without ever joining an organization. False while currentUser is loading.
  const isAdminUser =
    currentUser?.userType === 'operator_42a' || currentUser?.userType === 'agent_42a'

  // Check if we're on auth pages or onboarding pages
  const isAuthPage = pathname?.startsWith('/sign-in') || pathname?.startsWith('/sign-up')
  const isOnboardingPage = pathname?.startsWith('/onboarding')

  // Public pages legitimately viewed while signed out (mirrors middleware's
  // public routes). These render their own content without authed queries, so
  // the signed-out fallback below renders them directly instead of redirecting.
  const isPublicPage = pathname === '/' || pathname?.startsWith('/claim')

  // TODO(publisher-v1.1): Integrate publisher verification as a proper onboarding step so
  // dual-role users (brand customer + publisher) finish brand onboarding before reaching
  // /publisher, instead of bypassing it via this exemption. The plan explicitly resolved
  // this conflict as "V1 pragmatism — exemption is acceptable short-term" but flagged a
  // follow-up: add a Publisher Verification step inside the onboarding stepper and remove
  // this special-case. See docs/plans/2026-04-20-publisher-persona.md conflict resolution #4.

  // Publisher pages are accessible without completing brand onboarding.
  // This prevents ConditionalLayout from bouncing claimed-but-unverified publisher users
  // back to /onboarding whenever they try to access the /publisher/* tree, or the
  // top-level /verticals and /sources pages that were extracted from it.
  const isPublisherPage =
    pathname?.startsWith('/publisher') ||
    pathname?.startsWith('/verticals') ||
    pathname?.startsWith('/sources')

  // Partner portal is accessible without an org — partner users may have no
  // organizationId when they first sign in (before creating their first org).
  const isPartnerPortalPage = pathname?.startsWith('/partners')

  // Admin console + partner portal are exempt from the inactive-org gate: an
  // operator or partner must still reach their tools when the org they happen to
  // be viewing is inactive. Those routes do their own authorization (AdminLayout
  // redirects non-admins; the partner page checks partner access).
  const isAdminPage = pathname?.startsWith('/admin')
  const isInactiveExemptPage = isAdminPage || isPartnerPortalPage

  // "Arrived at the page a redirect was heading to" — used to clear isRedirecting.
  // MUST be portal-aware: on a portal host the only valid destination is that
  // portal's own route, so being on the OTHER portal's path (e.g. /partners while
  // on the admin host) does NOT count as arrived. Resetting there would clear
  // isRedirecting mid-flight and let the confinement effect restart the push on
  // every render — an infinite redirect/loader loop (admin.42a.ai/partners,
  // partners.42a.ai/admin). On the regular app host, keep the original set of
  // redirect destinations.
  const arrivedAtRedirectTarget =
    portal === 'admin'
      ? isAdminPage
      : portal === 'partners'
        ? isPartnerPortalPage
        : isOnboardingPage || isPublisherPage || isPartnerPortalPage || isAdminPage

  // Fire "Create User" the first time isSignedIn flips true after a sign-up
  // (the /sign-up page sets a sessionStorage marker on mount; we consume it here
  // so the event fires regardless of where Clerk redirects after signup).
  useEffect(() => {
    if (isLoaded && isSignedIn && consumeSignupPending()) {
      trackEvent(AnalyticsEvent.CreateUser)
    }
  }, [isLoaded, isSignedIn])

  // Handle redirect to onboarding in useEffect (not during render).
  // Skip redirect when the org is inactive or missing from our DB — those users
  // see InactiveAccountScreen instead. Also skip on a standalone portal host:
  // the portals are org-independent, and switching to an onboarding-incomplete
  // org (Clerk active-org change) must NOT drag the portal tab to /onboarding —
  // that route never renders on a portal host, so it would hang on a spinner.
  useEffect(() => {
    if (!portal && isSignedIn && !isInactive && !isMissingOrg && hasCompletedOnboarding === false && !isOnboardingPage && !isPublisherPage && !isRedirecting) {
      setIsRedirecting(true)
      router.push('/onboarding')
    }
  }, [portal, isSignedIn, isInactive, isMissingOrg, hasCompletedOnboarding, isOnboardingPage, isPublisherPage, isRedirecting, router])

  // Partner-portal redirect: partner users with no org (isMissingOrg) should land
  // on /partners. Non-partner users with no org keep the previous
  // InactiveAccountScreen behavior (isMissingOrg branch below handles that).
  // Never fire on the admin host: pushing /partners there can never satisfy
  // arrivedAtRedirectTarget (isAdminPage), so isRedirecting would never clear and
  // the visitor hangs on a spinner. The admin host's own redirect handles them.
  useEffect(() => {
    if (portal !== 'admin' && isSignedIn && isMissingOrg && isPartnerUser && !isPartnerPortalPage && !isRedirecting) {
      setIsRedirecting(true)
      router.push('/partners')
    }
  }, [portal, isSignedIn, isMissingOrg, isPartnerUser, isPartnerPortalPage, isRedirecting, router])

  // No-org super admin: an operator/agent with no organization would otherwise
  // dead-end on InactiveAccountScreen (the isMissingOrg branch below). Admin
  // access is org-independent, so send them to /admin instead. Fires on the admin
  // host and the regular app host — but NOT the partners host: a visitor who is
  // both an operator AND a partner would otherwise be dragged to /admin there,
  // where arrivedAtRedirectTarget (isPartnerPortalPage) can never be satisfied and
  // isRedirecting never clears (permanent spinner). On the partners host the
  // partner redirect above handles them.
  useEffect(() => {
    if (portal !== 'partners' && isSignedIn && isMissingOrg && isAdminUser && !isAdminPage && !isRedirecting) {
      setIsRedirecting(true)
      router.push('/admin')
    }
  }, [portal, isSignedIn, isMissingOrg, isAdminUser, isAdminPage, isRedirecting, router])

  // Portal-host confinement — "the URL decides the portal". On the admin host,
  // keep authorized admins inside /admin; on the partners host, keep partner
  // users inside /partners. This effect only acts when `portal` is set, and on a
  // portal host EVERY non-portal route (including /onboarding) must bounce back —
  // the portal is org-independent and never shows the onboarding flow. Sign-in/up
  // still render (handled before the signed-in branch); guarded on isRedirecting
  // to avoid fighting the effects above. Non-authorized visitors are handled by
  // the render-level denial below, not here.
  useEffect(() => {
    if (isAuthPage || isRedirecting) return
    if (portal === 'admin' && isSignedIn && isAdminUser && !isAdminPage) {
      setIsRedirecting(true)
      router.push('/admin')
    } else if (portal === 'partners' && isSignedIn && isPartnerUser && !isPartnerPortalPage) {
      setIsRedirecting(true)
      router.push('/partners')
    }
  }, [portal, isSignedIn, isAdminUser, isPartnerUser, isAdminPage, isPartnerPortalPage, isAuthPage, isRedirecting, router])

  // Signed out on a protected route, typically the brief window during
  // sign-out before the hard redirect from useHardSignOut lands. Send to
  // /sign-in so the authenticated subtree unmounts instead of staying mounted
  // and firing auth'd Convex queries. HARD navigation, not router.replace:
  // App Router navigations racing the sign-out render storm trip a known
  // Next.js bug (vercel/next.js#63121) where the Router component throws
  // React #310 in production, above every error boundary. See useHardSignOut.
  useEffect(() => {
    if (isLoaded && !isSignedIn && !isAuthPage && !isPublicPage) {
      window.location.replace('/sign-in')
    }
  }, [isLoaded, isSignedIn, isAuthPage, isPublicPage])

  // Reset isRedirecting once we land on the intended destination. Uses the
  // portal-aware arrivedAtRedirectTarget so a cross-portal path on a portal host
  // (e.g. /partners on the admin host) does NOT prematurely clear the flag and
  // restart the confinement redirect.
  useEffect(() => {
    if (arrivedAtRedirectTarget && isRedirecting) {
      setIsRedirecting(false)
    }
  }, [arrivedAtRedirectTarget, isRedirecting])

  // Show loading state while auth is loading
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // If on auth pages, show full-width layout without sidebar
  if (isAuthPage) {
    return <div className="h-full">{children}</div>
  }

  // If signed in, check onboarding status
  if (isSignedIn) {
    // Standalone portal hosts (admin.42a.ai / partners.42a.ai). Handled here,
    // before ANY customer-shell branch, so a portal host renders ONLY its portal
    // — no Sidebar, no platform navigation. Access is role-based and independent
    // of org state (an operator/partner with no org, or an inactive org, still
    // gets their tools). The wrong audience is denied outright (sign out only).
    if (portal === 'admin' || portal === 'partners') {
      // Wait for the role to resolve so we don't flash a denial at an
      // authorized user.
      if (currentUser === undefined) {
        return (
          <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--color-background)' }}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--color-lime)' }} />
          </div>
        )
      }
      const authorized = portal === 'admin' ? isAdminUser : isPartnerUser
      if (!authorized) {
        return (
          <PortalAccessDeniedScreen
            portal={portal}
            reason={isPartnerDisabled ? 'partner-disabled' : 'not-authorized'}
          />
        )
      }
      // Portals are desktop tools (same as /admin and /partners on the app host).
      if (isMobile) {
        return (
          <PortalShell portal={portal}>
            <MobileUnavailableNotice />
          </PortalShell>
        )
      }
      // Only render portal content when on the portal's own route. The
      // confinement effect above redirects any other path back to it; show a
      // spinner meanwhile rather than flashing a platform page inside the shell.
      const onPortalRoute = portal === 'admin' ? isAdminPage : isPartnerPortalPage
      if (!onPortalRoute) {
        return (
          <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--color-background)' }}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--color-lime)' }} />
          </div>
        )
      }
      return <PortalShell portal={portal}>{children}</PortalShell>
    }

    // Inactive org: render the full platform shell (so the sidebar org switcher
    // still works and operators/partners can reach their consoles) and gate page
    // content behind the checkout paywall instead of a full-screen block. Admin
    // and partner-portal routes are exempt and render their own content. Backend
    // queries throw for inactive orgs, so non-exempt page content is never
    // rendered (the paywall takes its place), and the sidebar relies only on
    // inactive-safe queries. Operators are also exempt so they can inspect the
    // org's state without being bounced to a paywall.
    if (isInactive && currentUser !== undefined && !isOperator) {
      // Phone-width shell for inactive orgs too, for consistency with the
      // main mobile branch. The exempt pages (/admin, /partners) are
      // desktop-only destinations on mobile, so they show the same
      // "not available on mobile" notice as everywhere else. The user menu in
      // MobileTopNav carries the org switcher, so a multi-org user stuck on an
      // inactive org can still switch to a working one from here.
      if (isMobile) {
        return (
          <HelpProvider>
            <div className="flex flex-col h-full">
              <MobileTopNav />
              <main className="flex-1 min-w-0" style={{ background: 'var(--color-background)' }}>
                <DesktopRecommendedBanner />
                {isInactiveExemptPage ? <MobileUnavailableNotice /> : <SuspensionPaywall />}
              </main>
            </div>
          </HelpProvider>
        )
      }
      return (
        <HelpProvider>
          <div className="flex h-full">
            <Sidebar onCollapseChange={setCollapsed} />
            <main className={`main-content ${collapsed ? 'sidebar-collapsed' : ''} flex-1 min-w-0`}>
              <DesktopRecommendedBanner />
              {isInactiveExemptPage ? (
                <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
              ) : (
                <SuspensionPaywall />
              )}
            </main>
          </div>
          <CommandPalette />
        </HelpProvider>
      )
    }
    if (isMissingOrg) {
      // Non-partner users with no org: keep the previous InactiveAccountScreen behavior.
      // Wait for currentUser to load so we can distinguish partner vs non-partner.
      if (currentUser === undefined) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )
      }

      // No-org super admin: an operator/agent with no org must still reach the
      // admin console. The no-org-operator redirect effect above sends them to
      // /admin; once there, render the shell so AdminLayout can load (it does
      // its own operator gate). Checked before the non-partner branch so a
      // no-org operator never sees the missing-org screen.
      if (isAdminUser) {
        if (isAdminPage) {
          return (
            <HelpProvider>
              <div className="flex h-full">
                <Sidebar onCollapseChange={setCollapsed} />
                <main className={`main-content ${collapsed ? 'sidebar-collapsed' : ''} flex-1 min-w-0`}>
                  <DesktopRecommendedBanner />
                  <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
                </main>
              </div>
              <CommandPalette />
            </HelpProvider>
          )
        }
        // Show spinner while the redirect to /admin fires.
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )
      }

      // A partner teammate whose partner a 42a operator disabled. They are not
      // isPartnerUser any more (the backend revoked the partner context) and
      // partner teammates typically never had an org of their own, so without
      // this they would land on "we couldn't find this organization" — the one
      // thing that is NOT wrong with their account. Same screen and wording the
      // partners host shows them.
      if (isPartnerDisabled) {
        return <PortalAccessDeniedScreen portal="partners" reason="partner-disabled" />
      }

      if (!isPartnerUser) {
        // Not a partner user — show the missing-org inactive screen.
        return <InactiveAccountScreen variant="missing" />
      }

      // Partner users have no Clerk org until they create their first org.
      // The partner-portal redirect useEffect above sends them to /partners.
      // If they're already there, render with sidebar so the page can load.
      if (isPartnerPortalPage) {
        return (
          <HelpProvider>
            <div className="flex h-full">
              <Sidebar onCollapseChange={setCollapsed} />
              <main className={`main-content ${collapsed ? 'sidebar-collapsed' : ''} flex-1 min-w-0`}>
                <DesktopRecommendedBanner />
                <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
              </main>
            </div>
            <CommandPalette />
          </HelpProvider>
        )
      }
      // Show spinner while the useEffect redirect fires.
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )
    }

    // Onboarding-incomplete user being redirected to /onboarding: render the
    // shared onboarding branding shell so the sidebar persists seamlessly from
    // sign-up through to the first onboarding screen (no blank/blue flash).
    if (hasCompletedOnboarding === false && !isOnboardingPage && !isPublisherPage) {
      return <OnboardingLoadingScreen />
    }

    // Otherwise (queries still resolving for a returning user, or redirecting):
    // a neutral, on-brand loader.
    if (
      hasCompletedOnboarding === undefined ||
      orgStatus === undefined ||
      isRedirecting
    ) {
      return (
        <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--color-background)' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--color-lime)' }}></div>
        </div>
      )
    }

    // If on onboarding pages, show full-width layout without sidebar
    if (isOnboardingPage) {
      return <div className="h-full">{children}</div>
    }

    // Wait for the org query to resolve before rendering to avoid a flash of
    // unblocked content when the org is suspended.
    if (organizations === undefined) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )
    }

    // Phone-width shell: no sidebar (primary nav is intentionally
    // unreachable on mobile — post-login users land on /dashboard and stay
    // there), a slim top nav with the logo and the user profile menu (org
    // switcher, theme, language, profile, sign out), the "built for desktop"
    // strip, and the assistant chat as a full-width strip pinned to the bottom
    // that expands to a full-screen chat when tapped.
    if (isMobile) {
      const showChatStrip = hasModule('assistant')
      // Only the dashboard and the pages reachable from the user profile menu
      // are part of the mobile experience. Any other route (deep links from
      // emails, typed URLs) shows a "not available on mobile" notice with a
      // way back to the dashboard.
      const isMobileAllowedPage =
        pathname?.startsWith('/dashboard') ||
        pathname?.startsWith('/profile') ||
        pathname?.startsWith('/settings')
      return (
        <HelpProvider>
          <div className="flex flex-col h-full">
            <MobileTopNav />
            <main
              className="flex-1 min-w-0"
              style={{
                background: 'var(--color-background)',
                paddingBottom: showChatStrip ? MOBILE_CHAT_STRIP_CLEARANCE : undefined,
              }}
            >
              <DesktopRecommendedBanner />
              <TrialBanner />
              <OutOfRunsBanner />
              {isSuspended && currentUser !== undefined && !isOperator ? (
                <SuspensionPaywall />
              ) : isMobileAllowedPage ? (
                <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
              ) : (
                <MobileUnavailableNotice />
              )}
            </main>
          </div>
          {showChatStrip && <MobileAssistantChat />}
        </HelpProvider>
      )
    }

    // Show sidebar + main content for authenticated users with completed onboarding
    return (
      <HelpProvider>
        <div className="flex h-full">
          <Sidebar onCollapseChange={setCollapsed} />
          <main className={`main-content ${collapsed ? 'sidebar-collapsed' : ''} flex-1 min-w-0`}>
            <DesktopRecommendedBanner />
            {/* TrialBanner self-guards to null once the org is suspended (its
                `trial.active` derivation already requires an unsuspended,
                non-exempt, unpaid org with a future endDate), so mounting it
                unconditionally here is safe: it never stacks with
                SuspensionPaywall in practice. Both banners can in principle
                render at once with DesktopRecommendedBanner (mobile,
                non-suspended trial) — they stack vertically above page
                content without overlapping, and each wraps its own text on
                narrow viewports instead of clipping. */}
            <TrialBanner />
            <OutOfRunsBanner />
            {isSuspended && currentUser !== undefined && !isOperator ? (
              <SuspensionPaywall />
            ) : (
              <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
            )}
          </main>
        </div>
        <CommandPalette />
        {/* Assistant widget — desktop/tablet only, gated on assistant module.
            Phone widths use the MobileAssistantChat strip above instead. */}
        {hasModule('assistant') && (
          <div className="hidden sm:block">
            <AssistantWidget />
          </div>
        )}
      </HelpProvider>
    )
  }
  
  // Signed out. Public pages (e.g. /claim) render their own content normally.
  if (isPublicPage) {
    return <div className="h-full">{children}</div>
  }

  // Signed out on a protected route: we got here mid sign-out, before Clerk's
  // afterSignOutUrl redirect (and the effect above) navigate to /sign-in. Do NOT
  // render the authenticated children — their auth'd Convex queries would throw
  // and crash the page. Show a neutral loader until the redirect lands.
  return (
    <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--color-background)' }}>
      <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--color-lime)' }}></div>
    </div>
  )
}